import { useEffect, useRef } from 'react';

const vertexShader = `
  varying vec2 vUv;
  uniform float uDistortion;
  uniform float uProgress;
  void main() {
    vUv = uv;
    vec3 displaced = position;
    displaced.z += sin(uv.x * 8.0 + uProgress * 3.0) * uDistortion * 0.025;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(displaced, 1.0);
  }
`;
const fragmentShader = `
  uniform sampler2D uTexture;
  uniform vec2 uCover;
  uniform float uZoom;
  uniform float uOpacity;
  uniform float uDistortion;
  uniform float uProgress;
  varying vec2 vUv;
  void main() {
    vec2 uv = (vUv - 0.5) * uCover / uZoom + 0.5;
    uv.x += sin(uv.y * 8.0 + uProgress * 3.0) * uDistortion * 0.012;
    gl_FragColor = texture2D(uTexture, clamp(uv, 0.001, 0.999));
    gl_FragColor.a *= uOpacity;
    #include <colorspace_fragment>
  }
`;

// Two lightweight planes share one geometry. Frames run only while interaction is settling.
function createScene(THREE, host, canvas, badge, properties, getIndex, motion) {
  const context = canvas.getContext('webgl2', { alpha: true, antialias: true, powerPreference: 'low-power' });
  if (!context) return null;
  const renderer = new THREE.WebGLRenderer({ canvas, context, alpha: true, antialias: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(35, 1, 0.1, 20);
  camera.position.z = 4;
  const geometry = new THREE.PlaneGeometry(1, 1, 24, 12);
  const materials = [0, 1].map(() => new THREE.ShaderMaterial({
    vertexShader, fragmentShader, transparent: true, depthWrite: false,
    uniforms: { uTexture: { value: null }, uCover: { value: new THREE.Vector2(1, 1) }, uZoom: { value: 1 }, uOpacity: { value: 1 }, uDistortion: { value: 0 }, uProgress: { value: 0 } },
  }));
  const planes = materials.map((material, index) => {
    const mesh = new THREE.Mesh(geometry, material);
    mesh.renderOrder = 2 - index;
    scene.add(mesh);
    return mesh;
  });
  const textures = [];
  const pointer = { x: 0, y: 0, hover: 0 };
  const damped = { x: 0, y: 0, hover: 0 };
  let frame = 0;
  let visible = true;
  let disposed = false;
  let loaded = false;
  let previousTime = 0;
  let width = 1;
  let height = 1;
  let viewWidth = 1;
  const viewHeight = 2 * Math.tan(THREE.MathUtils.degToRad(35 / 2)) * 4;

  function wake() {
    if (!disposed && visible && loaded && !document.hidden && !frame) frame = requestAnimationFrame(render);
  }
  function resize() {
    width = host.clientWidth;
    height = host.clientHeight;
    if (!width || !height) return;
    renderer.setSize(width, height, false);
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    viewWidth = viewHeight * camera.aspect;
    wake();
  }
  function render(time) {
    frame = 0;
    if (disposed || !visible || !loaded || document.hidden) return;
    const delta = Math.min((time - (previousTime || time - 16)) / 1000, 0.05);
    previousTime = time;
    ['x', 'y', 'hover'].forEach(key => { damped[key] = THREE.MathUtils.damp(damped[key], pointer[key], 9, delta); });
    const p = motion.progress;
    const direction = motion.direction;
    const strength = Math.min(motion.velocity / 2.5, 1);
    const wave = Math.sin(p * Math.PI);
    const hover = damped.hover;
    const rx = damped.y * THREE.MathUtils.degToRad(5);
    const ry = damped.x * THREE.MathUtils.degToRad(7);
    planes.forEach((plane, slot) => {
      const texture = textures[(getIndex() + slot) % properties.length];
      const uniforms = materials[slot].uniforms;
      uniforms.uTexture.value = texture;
      const imageAspect = texture.image.width / texture.image.height;
      const aspect = width / height;
      uniforms.uCover.value.set(Math.min(aspect / imageAspect, 1), Math.min(imageAspect / aspect, 1));
      uniforms.uZoom.value = 1 + hover * 0.04 + wave * 0.035;
      uniforms.uDistortion.value = wave * (0.35 + strength * 0.65);
      uniforms.uProgress.value = p;
      const scale = slot === 0 ? 1.002 + hover * 0.06 + p * 0.08 : 0.90 + p * 0.102 + hover * 0.06;
      plane.scale.set(viewWidth * scale, viewHeight * scale, 1);
      if (slot === 0) {
        plane.position.set(-direction * p * viewWidth * 0.85 - damped.x * 0.045, damped.y * 0.025, hover * 0.07 + wave * (0.45 + strength * 0.15));
        plane.rotation.set(rx, ry - direction * p * (0.20 + strength * 0.05), 0);
        uniforms.uOpacity.value = 1 - THREE.MathUtils.smoothstep(p, 0.35, 1);
      } else {
        plane.position.set(direction * (1 - p) * viewWidth * 0.85 - damped.x * 0.045, damped.y * 0.025, -0.8 * (1 - p) + hover * 0.07);
        plane.rotation.set(rx, ry + direction * (1 - p) * 0.20, 0);
        uniforms.uOpacity.value = THREE.MathUtils.smoothstep(p, 0, 0.65);
      }
    });
    badge.style.transform = `translate(${-damped.x * 9}px, ${-damped.y * 6}px)`;
    renderer.render(scene, camera);
    // Expose only presentation state for inspecting animation and fallbacks in the browser.
    host.dataset.progress = p.toFixed(3);
    host.dataset.tilt = `${planes[0].rotation.x.toFixed(3)},${planes[0].rotation.y.toFixed(3)}`;
    if (Math.abs(pointer.x - damped.x) + Math.abs(pointer.y - damped.y) + Math.abs(pointer.hover - hover) > 0.001) wake();
  }
  const resizeObserver = new ResizeObserver(resize);
  resizeObserver.observe(host);
  const loader = new THREE.TextureLoader();
  properties.forEach((property, index) => {
    loader.load(property.image, texture => {
      if (disposed) { texture.dispose(); return; }
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.anisotropy = Math.min(4, renderer.capabilities.getMaxAnisotropy());
      textures[index] = texture;
      if (properties.every((_, i) => textures[i])) {
        loaded = true;
        host.dataset.webgl = 'ready';
        resize();
      }
    }, undefined, () => dispose());
  });
  function contextLost(event) { event.preventDefault(); dispose(); }
  function visibilityChanged() { if (document.hidden) { cancelAnimationFrame(frame); frame = 0; } else wake(); }
  canvas.addEventListener('webglcontextlost', contextLost);
  document.addEventListener('visibilitychange', visibilityChanged);
  motion.listeners.add(wake);
  function dispose() {
    if (disposed) return;
    disposed = true;
    cancelAnimationFrame(frame);
    resizeObserver.disconnect();
    motion.listeners.delete(wake);
    canvas.removeEventListener('webglcontextlost', contextLost);
    document.removeEventListener('visibilitychange', visibilityChanged);
    textures.forEach(texture => texture.dispose());
    materials.forEach(material => material.dispose());
    geometry.dispose();
    renderer.dispose();
    host.dataset.webgl = 'fallback';
    badge.style.transform = '';
  }
  return {
    wake, dispose,
    point(x, y, hover) { pointer.x = x; pointer.y = y; pointer.hover = hover; wake(); },
    setVisible(value) { visible = value; if (value) wake(); else { cancelAnimationFrame(frame); frame = 0; } },
  };
}

export default function PropertyVisual({ properties, index, motion }) {
  const host = useRef(null);
  const canvas = useRef(null);
  const badge = useRef(null);
  const cursor = useRef(null);
  const fallback = useRef(null);
  const fallbackNext = useRef(null);
  const engine = useRef(null);
  const currentIndex = useRef(index);
  currentIndex.current = index;

  useEffect(() => { engine.current?.wake(); }, [index]);
  useEffect(() => {
    const fade = () => {
      fallback.current.style.opacity = 1 - motion.progress;
      fallbackNext.current.style.opacity = motion.progress;
    };
    fade();
    motion.listeners.add(fade);
    return () => motion.listeners.delete(fade);
  }, [index, motion]);
  useEffect(() => {
    const element = host.current;
    const canvasElement = canvas.current;
    const badgeElement = badge.current;
    const cursorElement = cursor.current;
    const card = element.closest('.property-card');
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    let disposed = false;
    let visible = false;
    let starting = false;
    let failed = false;
    async function start() {
      if (disposed || !visible || media.matches || engine.current || starting || failed) return;
      starting = true;
      try {
        const THREE = await import('three');
        if (disposed || media.matches) return;
        engine.current = createScene(THREE, element, canvasElement, badgeElement, properties, () => currentIndex.current, motion);
        if (!engine.current) { failed = true; element.dataset.webgl = 'fallback'; }
        engine.current?.setVisible(visible);
      } catch {
        failed = true;
        engine.current?.dispose();
        engine.current = null;
        element.dataset.webgl = 'fallback';
      } finally { starting = false; }
    }
    const observer = new IntersectionObserver(entries => {
      visible = entries[0].isIntersecting;
      engine.current?.setVisible(visible);
      if (visible) start();
    }, { rootMargin: '80px' });
    observer.observe(element);
    function preferenceChanged() {
      if (media.matches) { engine.current?.dispose(); engine.current = null; }
      else start();
    }
    function pointerMove(event) {
      if (!finePointer.matches || media.matches || event.pointerType === 'touch') return;
      const rect = element.getBoundingClientRect();
      const x = Math.min(1, Math.max(-1, (event.clientX - rect.left) / rect.width * 2 - 1));
      const y = Math.min(1, Math.max(-1, (event.clientY - rect.top) / rect.height * 2 - 1));
      engine.current?.point(x, y, 1);
      element.dataset.hoverDirection = x < -0.54 ? 'left' : x > 0.54 ? 'right' : 'drag';
      cursorElement.style.transform = `translate(${event.clientX - rect.left}px, ${event.clientY - rect.top}px)`;
    }
    function enter(event) { if (finePointer.matches && event.pointerType !== 'touch' && !media.matches) engine.current?.point(0, 0, 1); }
    function leave() { engine.current?.point(0, 0, 0); delete element.dataset.hoverDirection; }
    media.addEventListener('change', preferenceChanged);
    element.addEventListener('pointermove', pointerMove);
    card.addEventListener('pointerenter', enter);
    card.addEventListener('pointerleave', leave);
    return () => {
      disposed = true;
      observer.disconnect();
      media.removeEventListener('change', preferenceChanged);
      element.removeEventListener('pointermove', pointerMove);
      card.removeEventListener('pointerenter', enter);
      card.removeEventListener('pointerleave', leave);
      engine.current?.dispose();
      engine.current = null;
    };
  }, [properties, motion]);

  const property = properties[index];
  return (
    <div ref={host} className="property-visual" data-webgl="fallback">
      <div className="property-visual__images">
        <img ref={fallback} className="property-visual__fallback" src={property.image} alt={property.alt} width="800" height="450" loading="lazy" draggable="false" />
        <img ref={fallbackNext} className="property-visual__fallback property-visual__fallback--next" src={properties[(index + 1) % properties.length].image || property.image} alt="" aria-hidden="true" width="800" height="450" loading="lazy" draggable="false" />
      </div>
      <canvas ref={canvas} className="property-visual__canvas" aria-hidden="true" />
      <span ref={badge} className="property-visual__badge">{property.location}</span>
      <span ref={cursor} className="property-visual__cursor" aria-hidden="true" />
    </div>
  );
}
